import type { Client } from '@rendimees24/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';


import {
  createClient,
  createClientUser,
  fetchClient,
  searchEstonianCompanies,
  subscribeClients,
  type CreateClientInput,
  type CreateClientUserInput,
} from './clients';

/** Live-updating list of all clients. Real-time Firestore subscription. */
export function useClientsList() {
  const [clients, setClients] = useState<Client[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsub = subscribeClients(setClients);
    return () => {
      try {
        unsub();
      } catch (e) {
        setError(e as Error);
      }
    };
  }, []);

  return { clients, loading: clients === null && !error, error };
}

/**
 * EPIC 37 — count of clients with a pending contract-terms application, for the
 * admin nav badge. Live; reads via the all-clients subscription (admin-scoped).
 */
export function usePendingContractRequestCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const unsub = subscribeClients((cs) =>
      setCount(cs.filter((c) => c.contractRequest?.status === 'requested').length),
    );
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, []);
  return count;
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () => (id ? fetchClient(id) : Promise.resolve(null)),
    enabled: Boolean(id),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) => createClient(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useCreateClientUser(clientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateClientUserInput, 'clientId'>) => {
      if (!clientId) throw new Error('clientId is required');
      return createClientUser({ ...input, clientId });
    },
    onSuccess: () => {
      if (clientId) {
        void qc.invalidateQueries({ queryKey: ['client', clientId] });
      }
    },
  });
}

export function useAriregisterSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ariregister-search', query],
    queryFn: () => searchEstonianCompanies(query),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 5 * 60_000,
  });
}

